import type { paths } from "@connect/data/frames/schema";
import { forwardCredentials } from "../../lib/route-authorize";
import { updateObservabilityCustomerId } from "../../lib/observability-setup";
import type { MessageMeta } from "../../lib/external-messages.types";
import type { createExternalMessages } from "./external-messages";

type AuthorizeResponse =
  paths["/platform/v1/partner/authorize"]["post"]["responses"][200]["content"]["application/json"];

// Only the completions the auth routing emits — keeps the helper testable and
// makes it explicit that auth never emits `connectionRequired`.
type AuthEmitter = Pick<
  ReturnType<typeof createExternalMessages>,
  "active" | "pending" | "unavailable" | "termsAcceptanceRequired" | "connectionFailedGeneric"
>;

/**
 * Route a post-OTP authorize response for the **auth** frame. The customer has
 * just authenticated, so the contract differs from the check frame:
 *
 *   - active / kyc_required        -> active (the customer is authenticated; any
 *     KYC requirement rides in `capabilities`). Mirrors Orion, which collapsed
 *     every non-terms outcome to active.
 *   - terms_acceptance_required    -> termsAcceptanceRequired
 *   - pending                      -> pending with credentials, so the partner can
 *     quote and poll KYC status until it resolves
 *   - unavailable                  -> status-only (the connection isn't usable;
 *     never hand out credentials)
 *   - connection_required          -> generic error: nonsensical once the
 *     customer has authenticated, so we fail rather than tell the partner to
 *     relaunch the frame they're in
 *   - anything else                -> generic error (fail closed)
 */
export async function routeAuthConnection(
  data: AuthorizeResponse,
  out: AuthEmitter,
  publicKey: string,
  meta: MessageMeta = {},
): Promise<void> {
  // Sync customerId to observability before any status branch emits, so
  // postMessage logs are attributed on every outcome. See DEVXP-1985.
  updateObservabilityCustomerId(data.customerId);

  switch (data.status) {
    case "terms_acceptance_required":
      await out.termsAcceptanceRequired(meta);
      return;
    case "unavailable":
      await out.unavailable(meta);
      return;
    case "active":
    case "pending":
    case "kyc_required":
      break;
    case "connection_required":
      await out.connectionFailedGeneric("Unable to complete authentication.", {
        ...meta,
        reason: "connection_required",
      });
      return;
    default:
      await out.connectionFailedGeneric("Unable to complete authentication.", {
        ...meta,
        reason: "unknown_status",
      });
      return;
  }

  const { credentials, capabilities } = await forwardCredentials(data, publicKey);
  if (data.status === "pending") {
    await out.pending({ credentials, capabilities, customerId: data.customerId ?? "", meta });
    return;
  }
  await out.active({ credentials, capabilities, customerId: data.customerId ?? "", meta });
}
